import React from "react";
import { ACCENT, SectionTitle, monoFont } from "./SectionHeading";

// The example brand Fluvo "extracted" — used across the mock email and profile card.
const BRAND = "#2D6A4F";
const BRAND_COLORS = ["#2D6A4F", "#F4E8D1", "#1B1B1B", "#FFFFFF"];
const TONE_TAGS = ["Warm", "Friendly", "Approachable", "Playful", "Encouraging"];

const Field = ({ label, children }) => (
  <div>
    <p
      className="mb-2.5 text-[11px] uppercase tracking-[0.14em] text-neutral-400"
      style={monoFont}
    >
      {label}
    </p>
    {children}
  </div>
);

const AIEmailSection = () => {
  return (
    <section
      className="bg-[#fafafa] px-6 py-28 lg:px-10 lg:py-36"
      style={{ fontFamily: "var(--font-geist)" }}
    >
      <div className="mx-auto max-w-6xl">
        <SectionTitle
          eyebrow="AI-generated emails"
          title="Every email, written"
          em="by AI that knows your brand"
          body="Fluvo generates campaign emails from scratch, personalized for every segment, optimized for every send. No templates, no copywriting, no guesswork."
        />

        <div className="mt-16 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
          {/* Left: email preview mockup */}
          <div className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white lg:col-span-7">
            <div className="flex h-11 items-center gap-2 border-b border-neutral-200 px-4">
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-2.5 w-2.5 rounded-full bg-neutral-200"
                  />
                ))}
              </div>
              <span
                className="flex-1 text-center text-[11px] text-neutral-400"
                style={monoFont}
              >
                Email preview
              </span>
            </div>

            <div className="flex-1 p-7 lg:p-10">
              <div className="mb-6 border-b border-neutral-200 pb-6">
                <div className="mb-4 flex items-center gap-3">
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold text-white"
                    style={{ background: BRAND }}
                  >
                    B
                  </span>
                  <div>
                    <p className="text-[14px] font-medium text-neutral-900">
                      Bloom &amp; Co.
                    </p>
                    <p className="text-[12px] text-neutral-400">
                      hello@bloomandco.com
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-[18px] font-medium tracking-[-0.01em] text-neutral-900">
                    You left something behind, Sarah!
                  </p>
                  <span
                    className="inline-flex h-6 items-center rounded-full px-2 text-[11px] font-medium"
                    style={{
                      background: `${ACCENT}1f`,
                      color: ACCENT,
                      ...monoFont,
                    }}
                  >
                    AI generated
                  </span>
                </div>
              </div>

              <div className="space-y-4 text-[15px] leading-[1.6] text-neutral-600">
                <p>Hi Sarah,</p>
                <p>
                  We noticed you were checking out the{" "}
                  <span className="font-medium text-neutral-900">
                    Starter Kit Bundle
                  </span>{" "}
                  earlier today. Great choice! It's one of our most popular
                  picks this month.
                </p>
                <p>
                  Your cart is still waiting. Complete your order in the next 24
                  hours and enjoy{" "}
                  <span className="font-medium text-neutral-900">
                    free shipping
                  </span>{" "}
                  on us.
                </p>
                <span
                  className="inline-block h-10 rounded-lg px-5 text-[14px] font-medium leading-10 text-white"
                  style={{ background: BRAND }}
                >
                  Complete Your Order →
                </span>
                <p className="pt-2 text-[13px] text-neutral-400">
                  See you soon,
                  <br />
                  The Bloom &amp; Co. Team
                </p>
              </div>
            </div>
          </div>

          {/* Right: extracted brand profile */}
          <div className="flex flex-col rounded-2xl border border-neutral-200 bg-white lg:col-span-5">
            <div className="flex h-14 items-center justify-between border-b border-neutral-200 px-6">
              <span className="text-[14px] font-medium text-neutral-900">
                Extracted brand profile
              </span>
              <span
                className="inline-flex h-6 items-center gap-1.5 rounded-full border border-neutral-200 px-2 text-[11px] text-neutral-500"
                style={monoFont}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: ACCENT }}
                />
                Auto-detected
              </span>
            </div>

            <div className="space-y-6 p-6">
              <Field label="Logo">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-lg text-[18px] font-bold text-white"
                    style={{ background: BRAND }}
                  >
                    B
                  </span>
                  <div>
                    <p className="text-[14px] font-medium text-neutral-900">
                      Bloom &amp; Co.
                    </p>
                    <p className="text-[12px] text-neutral-400">
                      bloomandco.com
                    </p>
                  </div>
                </div>
              </Field>

              <Field label="Brand colors">
                <div className="flex gap-3">
                  {BRAND_COLORS.map((hex) => (
                    <div
                      key={hex}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <span
                        className="h-11 w-11 rounded-lg border border-neutral-200"
                        style={{ background: hex }}
                      />
                      <span
                        className="text-[10px] text-neutral-400"
                        style={monoFont}
                      >
                        {hex}
                      </span>
                    </div>
                  ))}
                </div>
              </Field>

              <Field label="Typography">
                <div className="grid gap-2">
                  <div className="flex h-11 items-baseline justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-3.5">
                    <span
                      className="self-center text-[15px] font-semibold text-neutral-900"
                      style={{ fontFamily: "Georgia, serif" }}
                    >
                      Georgia
                    </span>
                    <span
                      className="self-center text-[10px] text-neutral-400"
                      style={monoFont}
                    >
                      Headings
                    </span>
                  </div>
                  <div className="flex h-11 items-baseline justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-3.5">
                    <span className="self-center text-[15px] font-medium text-neutral-900">
                      Inter
                    </span>
                    <span
                      className="self-center text-[10px] text-neutral-400"
                      style={monoFont}
                    >
                      Body
                    </span>
                  </div>
                </div>
              </Field>

              <Field label="Tone of voice">
                <div className="flex flex-wrap gap-1.5">
                  {TONE_TAGS.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex h-7 items-center rounded-full border border-neutral-200 bg-white px-2.5 text-[12px] text-neutral-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </Field>

              <Field label="CTA style">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex h-9 items-center rounded-lg px-4 text-[12px] font-medium text-white"
                    style={{ background: BRAND }}
                  >
                    Shop Now →
                  </span>
                  <span
                    className="inline-flex h-9 items-center rounded-lg px-4 text-[12px] font-medium"
                    style={{ border: `1px solid ${BRAND}`, color: BRAND }}
                  >
                    Learn More
                  </span>
                </div>
              </Field>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIEmailSection;
